/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import React from "react";

import { Text, theme } from "@nordcloud/gnui";
import { KeyValue } from "~/generated/graphql";
import { getTextFromStringify } from "~/tools";
import { separateConnectedWords } from "~/views/plans/utils";

type KeyValueListProps = {
  items: KeyValue[];
  title: string;
  separateKeyToWords?: boolean;
  renderValue?: (value: string) => React.ReactNode;
};

export function KeyValueList({
  items,
  title,
  separateKeyToWords = true,
  renderValue = (value) => getTextFromStringify(value) || "—",
}: KeyValueListProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div
      css={{
        background: theme.color.background.ui01,
        borderRadius: theme.radius.md,
        border: theme.borders.grey,
        marginTop: theme.spacing.spacing03,
      }}
    >
      <div
        css={{
          padding: theme.spacing.spacing04,
          background: theme.color.background.ui03,
          borderBottom: theme.borders.grey,
        }}
      >
        <Text
          tag="div"
          size="md"
          weight="medium"
          color={theme.color.text.text01}
          lineHeight={"1.2rem"}
        >
          {title}
        </Text>
      </div>
      <div
        css={{
          padding: `${theme.spacing.spacing03} ${theme.spacing.spacing04}`,
        }}
      >
        {items.map((item) => (
          <div
            key={item.key}
            css={{
              "&:not(:last-child)": {
                marginBottom: theme.spacing.spacing01,
              },
            }}
          >
            <Text
              tag="span"
              color={theme.color.text.text01}
              css={{ marginBottom: theme.spacing.spacing01 }}
              data-testid={`key-${item.key}`}
            >
              {separateKeyToWords ? separateConnectedWords(item.key) : item.key}
              :{" "}
            </Text>
            <Text
              tag="span"
              color={theme.color.text.text02}
              data-testid={`value-${item.key}`}
            >
              {renderValue(item.value)}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}
